import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/data-grid-1-wrapper';
import * as React from 'react';
import * as _ from 'underscore';

import { ProfilePictureStyles } from '../../app/content/styles/ProfilePictureStyles';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { imgUrl } from '../../common/utilities/ImgUrl';
import { SelectedItemsScope } from '../../ts/controllers/ContentSubplaceCtrl';
import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { IUserInfo } from '../../ts/models/IUserInfo';
import { IFavoriteItem, IItemStatisticsInfo } from '../../ts/models/resourceInfoParts';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { IActionButton } from '../../ts/services/ActionPanelService';
import { CommonStyles } from './CommonStyles';
import { IActionTarget } from './components/ActionsMenu';
import { ActionsCell, ActionsCellOptions } from './components/dataGrid/ActionsCell';
import { DataGridCells } from './components/dataGrid/DataGridCells';
import {
  SelectableSortKeyColumnContext,
  SelectableSortKeyColumnItem,
  SelectableSortKeyColumnOptions,
  SelectableSortKeyDataGridColumn,
} from './components/dataGrid/SelectableSortKeyDataGridColumn';
import { ProfilePicture } from './components/ProfilePicture';
import { IReactTranslateService } from './IReactTranslateService';
import { QueryString } from './QueryString';
import { SorterActions } from './SorterActions';

export module CommonDataGridColumns {


  export function withExtraSpace(originalColumn: DataGrid.Column){
    return _.extend({},
      originalColumn,
      {
        preferredWidth: CommonStyles.Sizes.ColMaxWidth // If there is leftover space the column will expand up to this size)
      });
  }

  const ActionsCellStyleOverrides: React.CSSProperties = {
    paddingRight: 0,        // To put the actions menu up against the right edge of the column
    overflow: 'visible'     // To let the action menu extend beyond the row
  };

  export function withActions<TSummary, TDetail>(originalColumn: DataGrid.Column,
                              actionOptions: ActionsCellOptions<TSummary, TDetail>,
                              $scope: any,
                              isRowHeightTaller = false): DataGrid.Column {

    const cellFormatter = ActionsCell(
        originalColumn.cellFormatter,
        actionOptions,
        (row: DataGrid.Row) => selectOnly($scope, row),
        isRowHeightTaller ? CommonStyles.Sizes.DataGridWithPictureIconsCellHeight
                          : CommonStyles.Sizes.CellHeight,
        $scope);

    const styleOverrides = originalColumn.cellStyle ? { ...originalColumn.cellStyle, ...ActionsCellStyleOverrides }
                                                    : ActionsCellStyleOverrides;

    return {
      ...originalColumn,

      // TODO: TFS 769624 - Use the StyledCellFormatter instead of cellStyle
      // cellFormatter: StyledCellFormatter(actionsCellFormatter, styleOverrides),

      cellFormatter: cellFormatter,
      cellStyle: styleOverrides
    };
  }

  export interface ISelectColumnScope<T> extends ng.IScope, SelectedItemsScope<T> { }

  const ZeroPaddingStyleOverride: React.CSSProperties = {
    padding: 0
  };

  // The $scope parameter can be any scope that has a 'selectedItems' member.
  export function selected<T>(
      $scope: ISelectColumnScope<T>,
      testIdFn: (row: DataGrid.Row) => string = () => ''
  ): DataGrid.Column {

    const cellFormatter = DataGridCells.CheckboxCell({
      onCheckboxClicked: (row: DataGrid.Row) => toggleSelection($scope, row),
      checkboxCellTestIdFn: testIdFn,
      isRowSelected: (rowKey: string) => $scope.selectedItems.containsKey(rowKey)
    });

    return {
      name: 'selected-col',
      dataKey: 'selected',
      headerText: '',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: cellFormatter,
      resizable: false,
      sortable: false,
      // TODO: TFS 769624 - Use the StyledCellFormatter instead of cellStyle
      cellStyle: ZeroPaddingStyleOverride
    };
  }

  export interface IFavoriteColumnScope<T extends IFavoriteItem> extends ng.IScope {
    addFavorite(item: T): ng.IPromise<IResult>;
    removeFavorite(item: T): ng.IPromise<IResult>;
    dataGridProps: DataGrid.Props;
  }

  export function favorite<T extends IFavoriteItem>(
      $scope: IFavoriteColumnScope<T>,
      isFavoritable?: (row: DataGrid.Row) => boolean
  ): DataGrid.Column {

    const cellFormatter = DataGridCells.FavoriteCell({
      isFavoritable: (row: DataGrid.Row) => _.isFunction(isFavoritable) ? isFavoritable(row) : true,
      onAddFavorite: (row: DataGrid.Row) => addFavorite($scope, row),
      onRemoveFavorite: (row: DataGrid.Row) => removeFavorite($scope, row)
    });

    return {
      name: 'favorite-col',
      dataKey: 'favorite',
      headerText: '',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: cellFormatter,
      resizable: false,
      sortable: false,
      // TODO: TFS 769624 - Use the StyledCellFormatter instead of cellStyle
      cellStyle: ZeroPaddingStyleOverride
    };
  }

  function toggleSelection<T>($scope: ISelectColumnScope<T>, row: DataGrid.Row) {
    $scope.$apply(() => {
      $scope.selectedItems.toggle(row.data as T);
    });
  }

  function selectOnly<T>($scope: ISelectColumnScope<T>, row: DataGrid.Row) {
    $scope.$apply(() => {
      $scope.selectedItems.clear();
      $scope.selectedItems.add(row.data as T);
    });
  }

  function addFavorite<T extends IFavoriteItem>($scope: IFavoriteColumnScope<T>, row: DataGrid.Row) {
    $scope.$apply(() => {
      $scope.addFavorite(row.data as T);
      row.data.favorite = true;

      // TODO: replace this with a Flux store
      $scope.dataGridProps.hackyUpdate++;
    });
  }

  function removeFavorite<T extends IFavoriteItem>($scope: IFavoriteColumnScope<T>, row: DataGrid.Row) {
    $scope.$apply(() => {
      $scope.removeFavorite(row.data as T);
      row.data.favorite = false;

      // TODO: replace this with a Flux store
      $scope.dataGridProps.hackyUpdate++;
    });
  }

  export function owner(translate: IReactTranslateService,
                        siteUrls: SiteUrls,
                        getOwnerFn?: (row: DataGrid.Row) => IUserInfo): DataGrid.Column {
    return {
      name: 'owner-name-col',
      dataKey: 'ownerName',
      headerText: translate('column_owner'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
        const owner = getOwnerFn ? getOwnerFn(row) : row.data.owner;
        const displayName = owner ? owner.displayName : translate('unavailable');
        const userUrl = owner ? siteUrls.userUrl(owner.username, owner.domainName) : '';
        return {
          text: displayName,
          url: userUrl
        };
      })
    };
  }

  const AlertHeaderStyleOverrides: React.CSSProperties = {
    backgroundImage: imgUrl('alert_list_header_normal.svg'),
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: CommonStyles.Sizes.NormalIconBackgroundSize,
    width: CommonStyles.Sizes.NormalIconSize + 2 * CommonStyles.Sizes.MediumSpace
  };

  const AlertCellStyleOverrides: React.CSSProperties = {
    overflow: 'visible'     // To let the alert drop-down extend beyond the cell
  };

  export function alert(type: string, translate: IReactTranslateService): DataGrid.Column {
    return {
      name: 'has-alert-col',
      dataKey: 'hasAlert',
      headerText: '',
      direction: 'asc',
      headerTooltip: translate('column_has_alert'),
      resizable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.AlertCell(type),

      // TODO: TFS 769624 - Use the StyledCellFormatter/StyledHeaderFormatter instead of cellStyle/headerStyle
      headerStyle: AlertHeaderStyleOverrides,
      cellStyle: AlertCellStyleOverrides
    };
  }

  /**
   * Instantiates a "bar graph" column with a selectable field dropdown in the header,
   * which switches the field being displayed. The row entity type of the grid must implement
   * IItemStatisticsInfo, and the available fields must be present in the entity's usageInfoMaxValues
   * object.
   *
   * @param colName    A unique identifier for the column -- must be in camel case because it's used
   *                   in the query string
   * @param fieldKeys  An array of fields in the object to make available via the dropdown.
   *                   All these fields should be present in the row entity's usageInfoMaxValues.
   * @param defaultKey The default data key that should be selected in the column. This must be one
   *                   of the values in fieldKeys. If not specified, the first item will be used as
   *                   the default.
   *
   * The following parameters are dependencies that must be supplied to the column:
   * @param translate
   * @param sorterActions
   * @param browser
   */
  export function selectableColumnWithBars(colName: string,
                                           fieldKeys: string[],
                                           queryString: QueryString,
                                           translate: IReactTranslateService,
                                           sorterActions: SorterActions,
                                           defaultKey?: string): DataGrid.Column {

    const items: SelectableSortKeyColumnItem[] = _.map(fieldKeys, (fieldKey: string) => {
      return {
        dataKey: fieldKey,
        headerText: translate('column_' + fieldKey),
        cellFormatter: DataGridCells.LocalizedCountWithBar(
            (data: IItemStatisticsInfo<any>) => data.usageInfo[fieldKey],
            (data: IItemStatisticsInfo<any>) => data.usageInfoMaxValues[fieldKey])
      };
    });

    const columnContext: SelectableSortKeyColumnContext = { queryString, sorterActions };
    const columnOptions: SelectableSortKeyColumnOptions = {
      columnName: colName,
      defaultDataKey: defaultKey || fieldKeys[0],
      items: items
    };
    return SelectableSortKeyDataGridColumn(columnOptions, columnContext);
  }

  const userDisplayNameDisabledStyle = {
    color: CommonStyles.Colors.Grey102,
    textDecoration: 'none',
    pointerEvents: 'none'
  };

  export function userDisplayNameColumn<T extends IUserInfo>({
    actions,
    getActionTarget,
    linkDisabled,
    menuDisabled,
    scope,
    translate,
    url
  }: {
    actions: IActionButton<T, any>[];
    getActionTarget: () => IActionTarget<T, any>;
    linkDisabled?: (user: T) => boolean;
    menuDisabled?: (row: DataGrid.Row) => boolean;
    scope: DataGridCells.ISelectedItemsScope<T>;
    translate: IReactTranslateService;
    url: (user: T) => string;
    isRowHeightTaller?: boolean;
  }) {
    const compositeCellElements: DataGridCells.CellFormatterWithStyle[] = [{
      cellFormatter: DataGridCells.ProfilePictureCell((row: DataGrid.Row) => {
        const user = row.data as T;
        return {
          profileDisplayName: user.displayName,
          imageUrl: user.userImageUrl,
          divShape: ProfilePicture.Shape.Circle
        };
      }),
      cellChildStyle: ProfilePictureStyles.Layout.UserNameWithProfilePictureCellChildStyle
    }, {
      cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
        const user = row.data as T;
        const disabled = linkDisabled && linkDisabled(user);
        return {
          text: user.displayName,
          url: disabled ? null : url(user),
          style: disabled ? userDisplayNameDisabledStyle : null
        };
      }),
      cellChildStyle: ProfilePictureStyles.Layout.UserNameWithProfilePictureCellChildDisplayNameStyle
    }];

    const DisplayNameColumnWithProfileIconsCellFormatter = DataGridCells.CompositeCellWithCustomChildStyles(
      compositeCellElements,
      ProfilePictureStyles.Layout.UserNameWithProfilePictureCellWrapperStyle
    );

    const displayNameColumn = {
      name: 'displayName-col',
      dataKey: 'displayName',
      headerText: translate('column_fullName'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DisplayNameColumnWithProfileIconsCellFormatter
    };

    const isRowHeightTaller: boolean = true; // true for user display grid.

    return CommonDataGridColumns.withActions(
      displayNameColumn,
      { actions, getActionTarget, translate, menuDisabled },
      scope,
      isRowHeightTaller
    );
  }

}
